import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, CheckCircle2, Timer, Snowflake } from "lucide-react";

function Stat({ label, icon: Icon, value, unit, tone = "text-gray-900" }) {
    return (
        <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-1">
                <Icon className="w-3.5 h-3.5 text-blue-600" />
                {label}
            </div>
            <div className={`text-2xl font-semibold leading-tight ${tone}`}>
                {value}
                {unit && (
                    <span className="text-sm font-normal text-gray-400 ml-1">
                        {unit}
                    </span>
                )}
            </div>
        </div>
    );
}

function ActivityStatusBanner({ stats, loading }) {
    const { t } = useTranslation();

    if (loading && !stats) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="space-y-2">
                            <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
                            <div className="h-7 w-16 bg-gray-200 rounded animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!stats || stats.totalInvocations === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                <p className="text-sm text-gray-600 font-medium">
                    {t("activity.noActivity")}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                    {t("activity.noActivityDesc")}
                </p>
            </div>
        );
    }

    const successRate = (100 - (stats.errorRate || 0)).toFixed(1);
    const success = stats.totalInvocations - (stats.failureCount || 0);
    const isGood = (stats.errorRate || 0) <= 1;
    const total = stats.totalInvocations || 1;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <Stat
                    label={t("activity.requests") || "Requests"}
                    icon={TrendingUp}
                    value={stats.totalInvocations}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium mb-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />
                        {t("activity.success") || "Success"}
                    </div>
                    <div
                        className={`text-2xl font-semibold leading-tight ${isGood ? "text-green-700" : "text-amber-700"}`}
                    >
                        {successRate}
                        <span className="text-sm font-normal text-gray-400 ml-1">%</span>
                    </div>
                    <div className="mt-2 flex h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div className="bg-green-500" style={{ width: `${(success / total) * 100}%` }} />
                        {stats.failureCount > 0 && (
                            <div className="bg-red-500" style={{ width: `${(stats.failureCount / total) * 100}%` }} />
                        )}
                    </div>
                </div>
                <Stat
                    label={t("activity.p95") || "p95 latency"}
                    icon={Timer}
                    value={stats.p95Duration || 0}
                    unit="ms"
                    tone={stats.p95Duration > 2000 ? "text-amber-700" : "text-gray-900"}
                />
                <Stat
                    label={t("activity.coldStarts") || "Cold starts"}
                    icon={Snowflake}
                    value={stats.coldStartCount || 0}
                />
            </div>
        </div>
    );
}

export default ActivityStatusBanner;
